import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

import {
  RegistrationApi,
  RegistrationApplicationFormField,
  useRegistrationApplicationForm,
  useUpdateRegistrationForm,
} from "api/registrations";
import { useDictionaryValuesOptions } from "api/hooks";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { COURSE_FORM_FIELD_TYPES } from "consts";
import {
  PaziaButton,
  PaziaDateSelect,
  PaziaInput,
  PaziaLoader,
  PaziaSelect,
} from "components/atoms";
import { COURSE_FORM_FIELD_TYPES__DATES_TYPES } from "consts";
import { PaziaModal } from "components/molecules";

interface EditRegistrationModalProps {
  registration: RegistrationApi | null;
  onCloseRequest: () => void;
  onSuccess: () => void;
}

interface FormField {
  fieldId: number;
  value?: string;
}

// Heuristic: recognise the billing/payment group by its name so we can draw
// the admin's attention to it (this is the data used for invoicing the course).
const BILLING_GROUP_NAME_PATTERNS = [/rozlicz/i, /płatnoś/i, /platnosc/i, /faktur/i];
const isBillingGroup = (groupName: string | undefined) =>
  !!groupName && BILLING_GROUP_NAME_PATTERNS.some((re) => re.test(groupName));

const schema = yup.object().shape({
  fields: yup
    .array()
    .of(
      yup.object().shape({
        fieldId: yup.number().required(),
        value: yup.string(),
      }),
    )
    .required(),
});

interface FieldInputProps {
  field: RegistrationApplicationFormField;
  fieldIndex: number;
}

const DictionaryFieldInput = ({ field, fieldIndex }: FieldInputProps) => {
  const { loading, options } = useDictionaryValuesOptions(field.dictionaryId as number);
  return (
    <PaziaSelect
      name={`fields.${fieldIndex}.value`}
      options={options}
      isLoading={loading}
      disabled={!field.isEditable || loading}
      isClearable={!field.isRequired}
      placeholder="Wybierz..."
    />
  );
};

const FieldInput = ({ field, fieldIndex }: FieldInputProps) => {
  const disabled = !field.isEditable;
  const fieldType = field.type;
  const { register } = useFormContext();

  if (COURSE_FORM_FIELD_TYPES__DATES_TYPES.includes(fieldType)) {
    return <PaziaDateSelect name={`fields.${fieldIndex}.value`} disabled={disabled} />;
  }

  if (fieldType === COURSE_FORM_FIELD_TYPES.BOOLEAN) {
    return (
      <select
        disabled={disabled}
        className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 sm:text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:disabled:bg-slate-700/60 dark:disabled:text-slate-500"
        {...register(`fields.${fieldIndex}.value`)}
      >
        <option value="">Wybierz</option>
        <option value="true">Tak</option>
        <option value="false">Nie</option>
      </select>
    );
  }

  if (fieldType === COURSE_FORM_FIELD_TYPES.DICTIONARY && field.dictionaryId) {
    return <DictionaryFieldInput field={field} fieldIndex={fieldIndex} />;
  }

  if (fieldType === COURSE_FORM_FIELD_TYPES.NUMBER) {
    return <PaziaInput name={`fields.${fieldIndex}.value`} type="number" disabled={disabled} />;
  }

  if (fieldType === COURSE_FORM_FIELD_TYPES.USER_PHONE) {
    return (
      <PaziaInput
        name={`fields.${fieldIndex}.value`}
        disabled={disabled}
        placeholder="np. 500 600 700"
      />
    );
  }

  if (fieldType === COURSE_FORM_FIELD_TYPES.EMAIL) {
    return (
      <PaziaInput
        name={`fields.${fieldIndex}.value`}
        disabled={disabled}
        placeholder="adres@domena.pl"
      />
    );
  }

  return <PaziaInput name={`fields.${fieldIndex}.value`} disabled={disabled} />;
};

const EditRegistrationContent = ({
  registration,
  onSuccess,
  onCloseRequest,
}: {
  registration: RegistrationApi;
  onSuccess: () => void;
  onCloseRequest: () => void;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: applicationForm, loading } = useRegistrationApplicationForm(registration.userId, registration.courseId);
  const updateFormMutation = useUpdateRegistrationForm();

  const methods = useForm<{ fields: FormField[] }>({
    resolver: yupResolver(schema),
    defaultValues: {
      fields: [],
    },
  });

  React.useEffect(() => {
    if (applicationForm?.groups) {
      const formFields: FormField[] = [];
      applicationForm.groups.forEach((group) => {
        group.fields.forEach((field) => {
          formFields.push({
            fieldId: field.courseFormFieldId,
            value: field.value ?? "",
          });
        });
      });
      methods.reset({ fields: formFields });
    }
  }, [applicationForm, methods]);

  const onSubmit = async (data: { fields: FormField[] }) => {
    setIsSubmitting(true);
    try {
      // Send only editable fields back - the backend rejects read-only ones
      // and we don't want to accidentally stomp on server-managed values.
      const fieldsWithValues: { fieldId: number; value: string }[] = [];
      let fieldIndex = 0;

      applicationForm?.groups.forEach((group) => {
        group.fields.forEach((field) => {
          const formFieldValue = data.fields[fieldIndex];
          if (field.isEditable) {
            fieldsWithValues.push({
              fieldId: field.courseFormFieldId,
              value: formFieldValue?.value ?? "",
            });
          }
          fieldIndex++;
        });
      });

      await updateFormMutation.mutateAsync({
        studentId: registration.userId,
        courseId: registration.courseId,
        fields: fieldsWithValues,
      });
      toast.success("Karta zgłoszeniowa została zaktualizowana");
      onSuccess();
      onCloseRequest();
    } catch (error) {
      toast.error("Wystąpił błąd podczas zapisywania karty zgłoszeniowej");
      console.error("Error updating registration form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasEditableFields = !!applicationForm?.groups?.some((group) =>
    group.fields.some((field) => field.isEditable),
  );

  return (
    <FormProvider {...methods}>
      <ModalPadding form onSubmit={methods.handleSubmit(onSubmit)}>
        <ModalHeader>
          Edytuj kartę zgłoszeniową
          {(registration.studentFirstName || registration.studentSurname) && (
            <> - {registration.studentFirstName} {registration.studentSurname}</>
          )}
          {registration.courseName && (
            <span className="mt-1 block text-sm font-normal text-slate-500 dark:text-slate-400">
              Kurs: {registration.courseName}
            </span>
          )}
        </ModalHeader>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <PaziaLoader position="center" />
          </div>
        ) : (
          <div className="space-y-6">
            {applicationForm?.groups && applicationForm.groups.some((group) => group.fields.length > 0) ? (
              <div className="space-y-6">
                {applicationForm.groups.map((group, groupIndex) => {
                  const billing = isBillingGroup(group.name);
                  return (
                    <div
                      key={group.courseFormGroupId}
                      className={`space-y-4 rounded-lg p-4 ${
                        billing
                          ? "border border-amber-300 bg-amber-50/60 dark:border-amber-700 dark:bg-amber-900/10"
                          : "border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/40"
                      }`}
                    >
                      {group.name && (
                        <div className="flex items-center justify-between border-b border-slate-200 pb-2 dark:border-slate-700">
                          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">{group.name}</h3>
                          {billing && (
                            <span className="inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                              Dane do płatności
                            </span>
                          )}
                        </div>
                      )}
                      {group.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400">{group.description}</p>
                      )}
                      <div className="space-y-4">
                        {group.fields.map((field, fieldIndex) => {
                          // Calculate the global field index across all groups
                          let globalFieldIndex = 0;
                          for (let i = 0; i < groupIndex; i++) {
                            globalFieldIndex += applicationForm.groups[i].fields.length;
                          }
                          globalFieldIndex += fieldIndex;

                          return (
                            <div key={field.courseFormFieldId}>
                              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                {field.name}
                                {field.isRequired && <span className="ml-1 text-red-500">*</span>}
                                {!field.isEditable && (
                                  <span className="ml-2 text-xs font-normal text-slate-400 dark:text-slate-500">
                                    (tylko do odczytu)
                                  </span>
                                )}
                              </label>
                              <FieldInput field={field} fieldIndex={globalFieldIndex} />
                              {field.description && (
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{field.description}</p>
                              )}
                              {methods.formState.errors.fields?.[globalFieldIndex]?.value && (
                                <p className="mt-1 text-sm text-red-600">
                                  {methods.formState.errors.fields[globalFieldIndex]?.value?.message}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-slate-500 dark:text-slate-400">Brak pól formularza do edycji dla tej rejestracji.</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 border-t border-slate-200 pt-6 dark:border-slate-700">
              <PaziaButton type="secondary" onClick={onCloseRequest} disabled={isSubmitting}>
                Anuluj
              </PaziaButton>
              <PaziaButton
                type="primary"
                btnType="submit"
                disabled={isSubmitting || !hasEditableFields}
                processing={isSubmitting}
              >
                Zapisz zmiany
              </PaziaButton>
            </div>
          </div>
        )}
      </ModalPadding>
    </FormProvider>
  );
};

export const EditRegistrationModal = ({ registration, onCloseRequest, onSuccess }: EditRegistrationModalProps) => {
  return (
    <PaziaModal id="edit-registration-modal" isOpen={!!registration} onCloseRequest={onCloseRequest} size="lg">
      {registration ? (
        <EditRegistrationContent registration={registration} onSuccess={onSuccess} onCloseRequest={onCloseRequest} />
      ) : (
        <ModalPadding>
          <ModalHeader>Ładowanie karty zgłoszeniowej...</ModalHeader>
          <div className="flex h-64 items-center justify-center">
            <PaziaLoader position="center" />
          </div>
        </ModalPadding>
      )}
    </PaziaModal>
  );
};
